/**
 * Design tokens for the Digital Library.
 *
 * Values trace to the MLC (Joseph) Figma file, node 4579-259437. Anything a
 * component needs belongs here — a literal colour or spacing value in a component
 * is a defect.
 */

import type { Audience, Ministry } from "./audience"
import type { ThemeMode } from "./mode"

/** Every section on the page, in the ids its components already use. */
export type SectionId = "studies" | "events" | "ebooks" | "downloads" | "other-content" | "memberships" | "other"

/**
 * Every colour a component may reach for, named by the job it does rather than
 * by the value it happens to hold — `surfaceSubtle`, not `offWhite`. That is what
 * makes a second palette possible: a token called `white` cannot be `#262626` in
 * dark mode, and a component asking for `white` when it means "the fill behind a
 * text input" is the same defect as a hardcoded hex.
 *
 * Both palettes carry every key. A mode that omitted one would leave a component
 * styled with `undefined`, which CSS drops silently.
 */
export interface Palette {
  /** Page-level and card-level fills. */
  readonly surface: string
  readonly pageBackground: string
  /** The fill inside a control the user types in or opens — inputs, selects. */
  readonly controlSurface: string
  /** One step off `surface`: hover fills, table headers, the filter bar. */
  readonly surfaceSubtle: string

  readonly textPrimary: string
  readonly textSecondary: string
  readonly textMuted: string
  /** Text on a fill that opposes the page — the filled View control on `link`. */
  readonly textInverse: string
  /** Text and icons on an `accent` fill, which is amber in both modes. */
  readonly textOnAccent: string

  readonly border: string
  readonly borderStrong: string

  readonly accent: string
  readonly accentHover: string
  readonly link: string
  readonly linkHover: string

  readonly focusRing: string
  readonly error: string
  readonly errorSurface: string
  readonly success: string
  /** Pairs with `success` the way `errorSurface` pairs with `error` — the redemption confirmation. */
  readonly successSurface: string

  readonly skeletonBase: string
  readonly skeletonHighlight: string

  /**
   * POC only — the provenance overlay (`features/provenance`), which outlines
   * each element by the system its data came from. Not from Figma: nothing in the
   * design has a notion of "which API served this", because the whole point of
   * proposal 03 is that the customer cannot tell. Delete with the overlay.
   */
  readonly provenanceDce: string
  readonly provenanceMlc: string

  /** Detail pages. The OTT frame draws these dark; light mode lightens them. */
  readonly chipSurface: string
  readonly progressTrack: string
  readonly imageScrim: string
}

/** The Figma values. This is the palette the design was drawn in. */
const lightColor: Palette = {
  surface: "#ffffff",
  pageBackground: "#f2f2f2",
  controlSurface: "#ffffff",
  surfaceSubtle: "#f7f7f7",

  textPrimary: "#333333",
  textSecondary: "#666666",
  textMuted: "#8a8a8a",
  textInverse: "#ffffff",
  textOnAccent: "#333333",

  border: "#e0e0e0",
  borderStrong: "#c7c7c7",

  accent: "#f5a623",
  accentHover: "#dd9114",
  link: "#0a6ebd",
  linkHover: "#08558f",

  focusRing: "#0a6ebd",
  error: "#b3261e",
  errorSurface: "#fce8e6",
  success: "#1e7d43",
  successSurface: "#e6f4ea",

  skeletonBase: "#e8e8e8",
  skeletonHighlight: "#f4f4f4",

  provenanceDce: "#0a6ebd",
  provenanceMlc: "#a4508b",

  chipSurface: "#efefef",
  progressTrack: "rgba(255, 255, 255, 0.45)",
  imageScrim: "rgba(15, 20, 22, 0.28)",
}

/**
 * Figma draws no dark variant of the Digital Library, so this is a translation
 * rather than a transcription. Three rules kept it honest:
 *
 * 1. **Surfaces lighten as they rise.** `pageBackground` is the floor at
 *    `#121212`; a card sits above it at `#1e1e1e`; a control the user acts on
 *    sits above that. Dark mode has no shadows to speak of, so depth has to come
 *    from the fill.
 * 2. **AA, measured against the fill each colour actually lands on.** `textPrimary`
 *    on `surface` is 13.6:1; `textSecondary` 8.0:1; `link` on `pageBackground`
 *    8.2:1. Saturated light-mode colours (`error`, `success`, `focusRing`) go
 *    lighter and less saturated, because the same hue that reads on white
 *    vibrates on near-black.
 * 3. **Brand colours do not move.** `accent` is Lifeway amber in both modes, and
 *    the audience and section blocks below are untouched — they are the brand,
 *    not a theme. What changes is the text laid on them, which is why
 *    `textOnAccent` exists.
 */
const darkColor: Palette = {
  surface: "#1e1e1e",
  pageBackground: "#121212",
  controlSurface: "#2a2a2a",
  surfaceSubtle: "#262626",

  textPrimary: "#ededed",
  textSecondary: "#b5b5b5",
  textMuted: "#8f8f8f",
  /** Dark mode's `link` fill is pale, so the text on it goes dark — still "inverse". */
  textInverse: "#101010",
  textOnAccent: "#241a05",

  border: "#3a3a3a",
  borderStrong: "#4f4f4f",

  accent: "#f5a623",
  /** Hover brightens here rather than deepening: on near-black, a darker amber reads as disabled. */
  accentHover: "#ffc04d",
  link: "#6db3f2",
  linkHover: "#9acdf8",

  focusRing: "#7cc0ff",
  error: "#ff9a90",
  errorSurface: "#3a1a17",
  success: "#7ddb9e",
  successSurface: "#14301f",

  skeletonBase: "#262626",
  skeletonHighlight: "#333333",

  provenanceDce: "#4ea3e8",
  provenanceMlc: "#d08bbb",

  chipSurface: "#2b2b2b",
  /** Both of these sit over artwork rather than over a surface, so they stay dark-on-image. */
  progressTrack: "rgba(255, 255, 255, 0.32)",
  imageScrim: "rgba(0, 0, 0, 0.45)",
}

/**
 * The OTT detail frame layers its backdrop under a scrim that fades to the page
 * colour. Dark mode fades to near-black behind white text; light mode fades to
 * `pageBackground` behind `textPrimary`, which is why the hero copy stays legible
 * over artwork. The rgba stops are that mode's `pageBackground` at partial alpha.
 */
export interface Gradients {
  readonly heroFadeDown: string
  readonly heroFadeAcross: string
}

/** The scrim stops, as one expression per direction, for a given `pageBackground`. */
const heroFade = (r: number, g: number, b: number): Gradients => {
  const at = (alpha: number) => `rgba(${r}, ${g}, ${b}, ${alpha})`

  return {
    heroFadeDown: `linear-gradient(180deg, ${at(0)} 0%, ${at(0.55)} 40%, ${at(0.94)} 68%, ${at(1)} 100%)`,
    heroFadeAcross: `linear-gradient(90deg, ${at(0.92)} 0%, ${at(0.62)} 34%, ${at(0)} 66%)`,
  }
}

/** The colours a card wears: its block, the well behind its artwork, and text on both. */
export interface AudienceTone {
  readonly block: string
  readonly poster: string
  readonly label: string
  readonly labelMuted: string
}

/**
 * The audiences that name a real ministry. `adults` is absent deliberately: it
 * is what the classifier returns when nothing matched, not a ministry, and it
 * has no colour of its own — see `sectionTone`.
 *
 * `poster` is a deeper cut of `block`, not a second brand colour: every one is
 * its block at 0.82. It shows only where a card has no artwork, behind the
 * title drawn in its place.
 *
 * `label` is per audience because contrast demands it: white on Teal Wonder is
 * 2.4:1, which fails at the 11px the label runs at, so the teal takes a dark
 * label. Blue Ascent clears 9.0:1 and Grace Lavender 5.6:1 against white.
 */
export const audience: Record<Ministry, AudienceTone> = {
  students: { block: "#05bab4", poster: "#059994", label: "#173f3e", labelMuted: "rgba(23, 63, 62, 0.74)" },
  kids: { block: "#05bab4", poster: "#059994", label: "#173f3e", labelMuted: "rgba(23, 63, 62, 0.74)" },
  women: { block: "#7a599c", poster: "#644980", label: "#ffffff", labelMuted: "rgba(255, 255, 255, 0.78)" },
  men: { block: "#304d7a", poster: "#273f64", label: "#ffffff", labelMuted: "rgba(255, 255, 255, 0.78)" },
}

/**
 * A tone per section, worn by every card the classifier could not place — 83%
 * of the rows on the UAT account, which is why one shared `adults` colour made
 * the catch-all the loudest thing on the page and said nothing.
 *
 * Those cards show no audience label at all: `Adults` is the absence of a
 * ministry, and a label that appears on five cards in six is noise. So the
 * colour tells you which section you are in, and a label appears only where it
 * carries information — a Women's study inside Studies keeps Grace Lavender and
 * says so.
 *
 * Each `label` clears 4.5:1 on its own `poster` as well as on its `block`,
 * because the title a card draws when it has no artwork sits on the poster.
 */
export const sectionTone: Record<SectionId, AudienceTone> = {
  studies: { block: "#d4294d", poster: "#ae223f", label: "#ffffff", labelMuted: "rgba(255, 255, 255, 0.78)" },
  events: { block: "#549ed4", poster: "#4582ae", label: "#081015", labelMuted: "rgba(8, 16, 21, 0.74)" },
  ebooks: { block: "#cfbfa3", poster: "#aa9d86", label: "#38342c", labelMuted: "rgba(56, 52, 44, 0.74)" },
  downloads: { block: "#a3c230", poster: "#869f27", label: "#272f0c", labelMuted: "rgba(39, 47, 12, 0.74)" },
  "other-content": { block: "#f06654", poster: "#c55445", label: "#070303", labelMuted: "rgba(7, 3, 3, 0.74)" },
  /** The brand palette runs out here, so the last two take neutrals — an honest colour for a catch-all. */
  memberships: { block: "#666666", poster: "#545454", label: "#ffffff", labelMuted: "rgba(255, 255, 255, 0.78)" },
  other: { block: "#4a4a4a", poster: "#3d3d3d", label: "#ffffff", labelMuted: "rgba(255, 255, 255, 0.78)" },
}

/**
 * The brand secondaries, so an audience that splits out later takes a real
 * brand colour rather than an invented one. Rooted Radiance is also `adults`.
 */
export const brandSecondary = {
  harvestEmber: "#f06654",
  kingdomLight: "#549ed4",
  rootedRadiance: "#d4294d",
  newMercies: "#a3c230",
  desertSand: "#cfbfa3",
} as const

/**
 * Reserved heights that make every card in a row the same height whatever its
 * title length. Two lines of `fontSize.sm` at `lineHeight.tight`, and one line
 * of `fontSize.xs` at `lineHeight.normal`.
 */
export const card = {
  titleHeight: "2.1875rem",
  metaHeight: "1.125rem",
  /** Has to contain a header icon's padded hit area: 20px of glyph plus `spacing.xxs` on each side. */
  headerMinHeight: "1.75rem",
  /**
   * Artwork wells, by card family. Aspect is a property of the family rather
   * than of the item — each family has one source — so the section picks the
   * well and no card branches per item.
   *
   * `fit` follows from whether a family's sources all match its well. Studies
   * and Digital Events are exactly 16:9 from the DCE graph, so cropping them
   * takes nothing. Product art is 1:1 for 23 of 28 measured covers and portrait
   * for the rest, and ebook covers range from 0.665 to 0.778; those mat onto the
   * well rather than crop, because the minority shape would lose the edge of a
   * cover — and a cover's edge is where its title is.
   */
  artwork: {
    wide: { ratio: 16 / 9, fit: "cover" },
    square: { ratio: 1, fit: "contain" },
    portrait: { ratio: 2 / 3, fit: "contain" },
  },
} as const

/** One card family's artwork well. */
export type CardArtwork = (typeof card.artwork)[keyof typeof card.artwork]

/** How a section draws its cards. The section decides both; no card branches on its own content. */
export const sectionStyle: Record<SectionId, { readonly artwork: CardArtwork; readonly tone: AudienceTone }> = {
  studies: { artwork: card.artwork.wide, tone: sectionTone.studies },
  events: { artwork: card.artwork.wide, tone: sectionTone.events },
  ebooks: { artwork: card.artwork.portrait, tone: sectionTone.ebooks },
  downloads: { artwork: card.artwork.square, tone: sectionTone.downloads },
  "other-content": { artwork: card.artwork.square, tone: sectionTone["other-content"] },
  memberships: { artwork: card.artwork.square, tone: sectionTone.memberships },
  other: { artwork: card.artwork.square, tone: sectionTone.other },
}

export type SectionStyle = (typeof sectionStyle)[SectionId]

/** A real ministry brings its own colours; the catch-all wears the section's. */
export const toneFor = (who: Audience, section: AudienceTone): AudienceTone =>
  who === "adults" ? section : audience[who]

export const spacing = {
  none: "0",
  xxs: "0.25rem",
  xs: "0.5rem",
  sm: "0.75rem",
  md: "1rem",
  lg: "1.5rem",
  xl: "2rem",
  xxl: "3rem",
} as const

export const radius = {
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  pill: "624.9375rem",
} as const

export const fontFamily = {
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
} as const

export const fontSize = {
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.25rem",
  xl: "1.5rem",
  xxl: "2rem",
  display: "2.5rem",
} as const

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 800,
} as const

export const lineHeight = {
  tight: 1.25,
  normal: 1.5,
  loose: 1.75,
} as const

/** Minimum card width; the grid derives its column count from this. */
export const cardMinWidth = "10.5rem"

/**
 * A rail has no column constraint, so it pins each card to this instead of
 * `cardMinWidth`: at that width a 16:9 artwork well is only ~95px tall and the
 * cover art is unreadable. At 320px it is 180px tall, and a square well is
 * 320px. Showing fewer cards is the point — a rail wants its last card clipped,
 * since that is the affordance saying it scrolls.
 */
export const railCardWidth = "20rem"

/** Detail-page sizing. Figma draws the hero on a 1920 canvas; these are the 1440 equivalents. */
export const detail = {
  heroMinHeight: "31rem",
  /** Figma's 753px copy column, held so long descriptions stay readable. */
  copyMaxWidth: "42rem",
  /** Figma's 545px session card. */
  sessionCardWidth: "21.25rem",
} as const

/**
 * The player is the one dark surface in the app.
 *
 * The Digital Library renders the OTT frames in light mode, but the player draws
 * over video and the OTT file (`tD9flx0HYKcunYJJV1NhEp`, nodes 131-2050 /
 * 400-3126 / 400-2757) keeps it dark — there is no light variant to translate,
 * and inventing one would put dark text over arbitrary video frames. These are
 * the OTT variables verbatim, scoped here so nothing outside the player reaches
 * for them by accident.
 */
export const player = {
  /** `surface/backgrounds/app/bg-body` — behind the video, and the letterbox bars. */
  surface: "#161616",
  /** `surface/backgrounds/bg-container-75` — the settings panel. */
  panel: "#212121",
  panelBorder: "#3a3a3a",
  /** `semantic/surface/background/surface-section-bg` at the panel's depth. */
  tile: "#2b363b",
  /** `component/atom/progress-bar/progress-bar-track`, and the duration tag fill. */
  track: "#2b363b",
  /** `semantic/fill/brand/fill-brand-primary` — the played portion and the handle. */
  brand: "#fdb528",
  /** `semantic/content/on-fill/content-on-brand` — text and icons on `brand`. */
  onBrand: "#323e43",
  textPrimary: "#ffffff",
  /** `semantic/content/role/content-strong` — the dimmed "All Sessions" tab. */
  textStrong: "#929da3",
  /** `border/border-ada-yellow` — focus, which must clear `brand` on `brand`. */
  focusRing: "#fec747",
  /** The scrim the session browser drops over the video. */
  blanket: "rgba(22, 22, 22, 0.82)",

  /**
   * The chrome's safe area. Shared rather than local to the controls because the
   * session rail has to line its cards up with the controls above it while
   * itself spanning the full width — it applies this as scroll padding, not as a
   * margin, so cards travel to the screen edge instead of into a hidden box.
   */
  edgeInline: "clamp(1rem, 5vw, 5rem)",
  edgeBlock: "clamp(1rem, 4vh, 2.5rem)",
  /** `progress-lg-track-size`. */
  trackHeight: "0.75rem",
  /**
   * Figma draws a 420px session card on a 1920 TV canvas, where the rail shows
   * about four. Scaled to 320px here: a browser window is smaller and shared
   * with the rest of the chrome, and a card this size still shows the artwork
   * clearly while fitting roughly five across.
   */
  sessionCardWidth: "20rem",
} as const

/**
 * The player's chrome sits over video, so its readability comes from a scrim
 * rather than from a background colour. Figma draws one radial gradient behind
 * everything; splitting it top and bottom keeps the middle of the frame clear.
 */
export const playerGradient = {
  top: "linear-gradient(180deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.34) 45%, rgba(0, 0, 0, 0) 100%)",
  bottom: "linear-gradient(0deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.42) 45%, rgba(0, 0, 0, 0) 100%)",
} as const

export const breakpoint = {
  sm: "40rem",
  md: "64rem",
  lg: "90rem",
} as const

export const mediaUp = {
  sm: `@media (min-width: ${breakpoint.sm})`,
  md: `@media (min-width: ${breakpoint.md})`,
  lg: `@media (min-width: ${breakpoint.lg})`,
} as const

export const zIndex = {
  base: 0,
  sticky: 10,
  overlay: 100,
} as const

/** Everything that is the same in both modes: brand, type, space, geometry. */
const constants = {
  audience,
  sectionTone,
  sectionStyle,
  brandSecondary,
  card,
  spacing,
  radius,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  cardMinWidth,
  railCardWidth,
  detail,
  player,
  playerGradient,
  breakpoint,
  mediaUp,
  zIndex,
} as const

const palette: Record<ThemeMode, { readonly color: Palette; readonly gradient: Gradients }> = {
  light: { color: lightColor, gradient: heroFade(242, 242, 242) },
  dark: { color: darkColor, gradient: heroFade(18, 18, 18) },
}

/**
 * The theme for one mode. Components read `theme.color.*` and never the mode
 * itself — `mode` is carried for the rare style that cannot be expressed as a
 * colour swap, such as a shadow that has to become a border on a dark surface.
 */
export const themeFor = (mode: ThemeMode) => ({ mode, ...palette[mode], ...constants })

export type AppTheme = ReturnType<typeof themeFor>

/**
 * The default theme. Light, because that is the mode Figma draws and the one the
 * app falls back to when the browser states no preference. Anything rendering for
 * real goes through `ThemeModeProvider`, which picks the mode.
 */
export const theme = themeFor("light")
